import React from "react";
import { connect } from "react-redux";
import { Form, Col, Row } from "react-bootstrap";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { NumericFormat as NumberFormat } from "react-number-format";
import Select from "react-select";

import Modal from "../../../layout/modal";

import { get as getAccounts } from "../../../actions/accounts";
import { get as getFinancialAccounts } from "../../../actions/financial.accounts";
import { save as saveBusinessExpense } from "../../../actions/business.expenses";
import { search as searchVendors } from "../../../actions/vendors";
import { get as getSetting } from "../../../actions/settings";
import {
  next as getNextCheckNumber,
  collisions as getCheckCollisions,
  markPrinted,
} from "../../../actions/check.register";
import { render as renderReport } from "../../../actions/report.template";
import { loading } from "../../../actions/page";
import { downloadCheckPdf } from "../../../utils/checks";
import { format as formatDate } from "../../../utils/date";
import { EDIT_CHECK, EDIT_EXPENSE } from "../../../constants/permissions";

const safeParseJson = (value, fallback = {}) => {
  try {
    return typeof value === "string" ? JSON.parse(value) : (value || fallback);
  } catch (_err) {
    return fallback;
  }
};

const toCaption = (value) => String(value || "")
  .toLowerCase()
  .split("_")
  .filter(Boolean)
  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
  .join(" ");

const initialData = () => ({
  entryDate: new Date(),
  amount: 0,
  expenseAccountUuid: null,
  financialAccountUuid: null,
  vendorUuid: null,
  payeeName: "",
  checkNumber: "",
  checkDate: new Date(),
  memo: "",
  referenceNumber: "",
});

class BusinessExpenseModal extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      data: initialData(),
      referenceCheckLinkSource: null,
      accounts: [],
      financialAccounts: [],
      vendors: [],
      accountsSetting: {},
      error: null,
      warning: null,
    };
  }

  componentDidMount = async () => {
    if (this.props.show) await this.loadData();
  };

  componentDidUpdate = async (oldProps) => {
    if (!oldProps.show && this.props.show) await this.loadData();

    if (
      this.props.show
      && (
        oldProps.defaultExpenseAccountUuid !== this.props.defaultExpenseAccountUuid
        || oldProps.defaultFinancialAccountUuid !== this.props.defaultFinancialAccountUuid
        || oldProps.defaultAccountingAccountUuid !== this.props.defaultAccountingAccountUuid
      )
    ) {
      this.applyDefaults(this.state.accounts, this.state.financialAccounts);
    }
  };

  loadData = async () => {
    this.props.loading(true);

    const [accounts, financialAccounts, vendors, accountsSetting] = await Promise.all([
      this.props.getAccounts(null),
      this.props.getFinancialAccounts(null),
      this.props.searchVendors({ page: 0, size: 500, search: "" }),
      this.props.getSetting({ authentic: true, name: "ACCOUNTS" }),
    ]);

    this.setState({
      accounts: accounts || [],
      financialAccounts: financialAccounts || [],
      vendors: vendors?.items || vendors || [],
      accountsSetting: safeParseJson(accountsSetting?.ACCOUNTS || accountsSetting?.value || accountsSetting, {}),
      error: null,
    }, () => {
      this.applyDefaults(accounts || [], financialAccounts || []);
    });

    this.props.loading(false);
  };

  applyDefaults = (accounts = [], financialAccounts = []) => {
    const generalSettings = this.state.accountsSetting?.general || {};
    const configuredPaidFromAccountUuid = generalSettings.from || null;
    const configuredExpenseAccountUuid = generalSettings.expense || null;
    const financialAccountUuid = this.props.defaultFinancialAccountUuid
      || financialAccounts.find((account) => account.accountingAccountUuid === configuredPaidFromAccountUuid)?.uuid
      || financialAccounts.find((account) => account.accountingAccountUuid === this.props.defaultAccountingAccountUuid)?.uuid
      || null;

    const defaultAccount = accounts.find((account) => account.uuid === this.props.defaultAccountingAccountUuid);
    const expenseAccountUuid = this.props.defaultExpenseAccountUuid
      || configuredExpenseAccountUuid
      || (["EXPENSE", "COGS"].includes(defaultAccount?.type) ? defaultAccount.uuid : null);

    this.setState({
      data: {
        ...initialData(),
        expenseAccountUuid,
        financialAccountUuid,
      },
      referenceCheckLinkSource: null,
    }, this.applyNextCheckNumber);
  };

  applyNextCheckNumber = async () => {
    if (!this.canEditChecks || !this.state.data?.financialAccountUuid) return;
    if (String(this.state.data?.checkNumber || "").trim()) return;

    const result = await this.props.getNextCheckNumber({
      financialAccountUuid: this.state.data.financialAccountUuid,
    });
    if (!result?.nextCheckNumber) return;

    this.setState((prev) => ({
      data: {
        ...prev.data,
        checkNumber: result.nextCheckNumber,
        referenceNumber: prev.data.referenceNumber || result.nextCheckNumber,
      },
      referenceCheckLinkSource: "checkNumber",
    }));
  };

  accountLabel = (account) => {
    const number = String(account?.accountNumber || "").trim();
    const name = String(account?.name || "").trim();
    const purpose = String(account?.purpose || "").trim();
    const caption = [number, name].filter(Boolean).join(" - ") || account?.uuid || "";
    return purpose ? `${caption} (${toCaption(purpose)})` : caption;
  };

  get expenseAccountOptions() {
    return (this.state.accounts || [])
      .filter((account) => account?.uuid && ["EXPENSE", "COGS"].includes(account.type))
      .map((account) => ({ value: account.uuid, label: this.accountLabel(account) }));
  }

  get selectedExpenseAccountOption() {
    return this.expenseAccountOptions.find((option) => option.value === this.state.data?.expenseAccountUuid) || null;
  }

  get financialAccountOptions() {
    return (this.state.financialAccounts || [])
      .filter((account) => account?.uuid && account.isActive)
      .map((account) => ({
        value: account.uuid,
        label: [
          account.name,
          account.accountType ? toCaption(account.accountType) : null,
          account.lastFour ? `*${account.lastFour}` : null,
        ].filter(Boolean).join(" - "),
      }));
  }

  get selectedFinancialAccountOption() {
    return this.financialAccountOptions.find((option) => option.value === this.state.data?.financialAccountUuid) || null;
  }

  vendorLabel = (vendor) => vendor?.Person?.displayName || vendor?.displayName || vendor?.uuid || "";

  get vendorOptions() {
    return (this.state.vendors || [])
      .filter((vendor) => vendor?.uuid)
      .map((vendor) => ({
        ...vendor,
        value: vendor.uuid,
        label: this.vendorLabel(vendor),
      }));
  }

  get selectedVendorOption() {
    return this.vendorOptions.find((option) => option.value === this.state.data?.vendorUuid) || null;
  }

  onVendorChange = (option) => {
    this.setState((prev) => ({
      data: {
        ...prev.data,
        vendorUuid: option?.value || null,
        payeeUuid: option?.value || null,
        payeeName: option?.label || "",
        payeeType: option?.value ? "VENDOR" : null,
        expenseAccountUuid: option?.expenseAccountUuid || prev.data?.expenseAccountUuid || null,
      },
    }));
  };

  onChange = (e) => {
    const { name, value } = e.target;
    if (["checkNumber", "checkDate"].includes(name) && !this.canEditCheckDetails) return;

    this.setState((prev) => {
      const data = {
        ...prev.data,
        [name]: value,
      };
      let referenceCheckLinkSource = prev.referenceCheckLinkSource;
      const linkedField = this.canEditCheckDetails && name === "referenceNumber"
        ? "checkNumber"
        : name === "checkNumber"
          ? "referenceNumber"
          : null;

      if (linkedField) {
        const linkedValue = String(prev.data?.[linkedField] || "");

        if (!referenceCheckLinkSource && !linkedValue.trim()) {
          referenceCheckLinkSource = name;
        }

        if (referenceCheckLinkSource === name) {
          data[linkedField] = value;
        } else if (referenceCheckLinkSource) {
          referenceCheckLinkSource = null;
        }
      }

      return { data, referenceCheckLinkSource };
    }, () => {
      if (name === "financialAccountUuid") this.applyNextCheckNumber();
      if (name === "checkNumber" || name === "financialAccountUuid") this.checkNumberCollision();
    });
  };

  checkNumberCollision = async () => {
    const { financialAccountUuid, checkNumber } = this.state.data || {};
    if (!this.canEditCheckDetails || !financialAccountUuid || !String(checkNumber || "").trim()) {
      this.setState({ warning: null });
      return;
    }

    const result = await this.props.getCheckCollisions({ financialAccountUuid, checkNumber });
    this.setState({ warning: result?.warning ? result.message : null });
  };

  onCancel = () => {
    this.setState({ data: initialData(), referenceCheckLinkSource: null, error: null, warning: null });
    this.props.onCancel?.();
  };

  onSave = async ({ writeCheck = false } = {}) => {
    const data = this.state.data || {};

    if (!data.expenseAccountUuid) {
      this.setState({ error: "Expense account is required." });
      return;
    }

    if (Number(data.amount || 0) <= 0) {
      this.setState({ error: "Amount must be greater than zero." });
      return;
    }

    if (!data.vendorUuid) {
      this.setState({ error: "Vendor is required." });
      return;
    }

    if (writeCheck && !this.canEditChecks) {
      this.setState({ error: "You do not have permission to write checks." });
      return;
    }

    if (writeCheck) await this.checkNumberCollision();

    this.props.loading(true);
    const payload = {
      ...data,
      entryDate: data.entryDate,
      amount: Number(data.amount || 0),
      payeeUuid: data.vendorUuid,
      payeeType: "VENDOR",
    };
    if (!this.canEditCheckDetails) {
      delete payload.checkNumber;
      delete payload.checkDate;
    }
    const result = await this.props.saveBusinessExpense(payload);
    this.props.loading(false);

    if (result?.success === false || !result) {
      this.setState({ error: result?.error?.message || "The business expense could not be saved." });
      return;
    }

    if (writeCheck) {
      try {
        await this.writeCheck(result);
      } catch (_err) {
        this.setState({ error: "The expense was saved, but the check could not be rendered." });
        return;
      }
    }

    this.setState({ data: initialData(), referenceCheckLinkSource: null, error: null, warning: null });
    this.props.onSaved?.(result);
  };

  writeCheck = async (result) => {
    const check = result?.check || {};
    await downloadCheckPdf({
      render: this.props.renderReport,
      templateUuid: this.state.accountsSetting?.general?.check,
      fileName: `${formatDate(check.checkDate)} Check ${check.checkNumber || check.payeeName || "Business Expense"}.pdf`,
      check,
    });
    await this.props.markPrinted({
      journalEntryUuid: result.journalEntryUuid,
      financialAccountUuid: this.state.data?.financialAccountUuid,
      checkNumber: check.checkNumber,
      checkDate: check.checkDate,
    });
  };

  get canEditChecks() {
    return (this.props.auth?.permissions || []).includes(EDIT_CHECK);
  }

  get canEditCheckDetails() {
    return (this.props.auth?.permissions || []).some((permission) => [EDIT_EXPENSE, EDIT_CHECK].includes(permission));
  }

  render() {
    const d = this.state.data || {};

    return (
      <Modal
        show={this.props.show}
        title="Business Expense"
        size="lg"
        onCancel={this.onCancel}
        buttons={[
          {
            caption: "write check",
            onClick: () => this.onSave({ writeCheck: true }),
            variant: "outline-primary",
            display: true,
            enabled: this.canEditChecks && Boolean(d.vendorUuid) && Number(d.amount || 0) > 0,
            icon: "print",
          },
          {
            caption: "ok",
            onClick: () => this.onSave(),
            variant: "primary",
            display: true,
            enabled: true,
          },
          {
            default: true,
            caption: "cancel",
            onClick: this.onCancel,
            variant: "secondary",
            display: true,
            enabled: true,
          },
        ]}
      >
        <Row>
          {this.state.error ? (
            <Col xs="12">
              <div className="alert alert-danger mb-3">{this.state.error}</div>
            </Col>
          ) : null}
          {this.state.warning ? (
            <Col xs="12">
              <div className="alert alert-warning mb-3">{this.state.warning}</div>
            </Col>
          ) : null}

          <Col xs="12" md="4">
            <div className="mb-3 top-label">
              <DatePicker
                id="businessExpenseEntryDate"
                name="entryDate"
                className="form-control"
                selected={d.entryDate ? new Date(d.entryDate) : null}
                onChange={(date) => this.onChange({ target: { name: "entryDate", value: date } })}
              />
              <Form.Label>DATE</Form.Label>
            </div>
          </Col>

          <Col xs="12" md="4">
            <div className="mb-3 top-label">
              <NumberFormat
                name="amount"
                id="businessExpenseAmount"
                value={d?.amount ?? ""}
                thousandSeparator
                allowNegative={false}
                inputMode="decimal"
                decimalScale={2}
                prefix="$"
                className="text-end form-control mb-2"
                onValueChange={({ floatValue }) => {
                  this.onChange({ target: { name: "amount", value: floatValue ?? 0 } });
                }}
              />
              <Form.Label>AMOUNT</Form.Label>
            </div>
          </Col>

          <Col xs="12" md="4">
            <div className="mb-3 top-label">
              <Form.Control
                type="text"
                name="referenceNumber"
                value={d.referenceNumber || ""}
                onChange={this.onChange}
              />
              <Form.Label>REFERENCE</Form.Label>
            </div>
          </Col>

          <Col xs="12" md="6">
            <div className="mb-3 top-label">
              <Select
                inputId="businessExpenseVendorUuid"
                classNamePrefix="react-select"
                isClearable
                placeholder="(Select vendor)"
                options={this.vendorOptions}
                value={this.selectedVendorOption}
                onChange={this.onVendorChange}
              />
              <Form.Label>VENDOR</Form.Label>
            </div>
          </Col>

          <Col xs="12" md="3">
            <div className="mb-3 top-label">
              <Form.Control
                type="text"
                name="checkNumber"
                value={d.checkNumber || ""}
                disabled={!this.canEditCheckDetails}
                onChange={this.onChange}
              />
              <Form.Label>CHECK NUMBER</Form.Label>
            </div>
          </Col>

          <Col xs="12" md="3">
            <div className="mb-3 top-label">
              <DatePicker
                id="businessExpenseCheckDate"
                name="checkDate"
                className="form-control"
                selected={d.checkDate ? new Date(d.checkDate) : null}
                disabled={!this.canEditCheckDetails}
                onChange={(date) => this.onChange({ target: { name: "checkDate", value: date } })}
              />
              <Form.Label>CHECK DATE</Form.Label>
            </div>
          </Col>

          <Col xs="12" md="6">
            <div className="mb-3 top-label">
              <Select
                inputId="businessExpenseAccountUuid"
                classNamePrefix="react-select"
                isClearable
                placeholder="(Select expense account)"
                options={this.expenseAccountOptions}
                value={this.selectedExpenseAccountOption}
                onChange={(option) => this.onChange({ target: { name: "expenseAccountUuid", value: option?.value || null } })}
              />
              <Form.Label>EXPENSE ACCOUNT</Form.Label>
            </div>
          </Col>

          <Col xs="12" md="6">
            <div className="mb-3 top-label">
              <Select
                inputId="businessExpenseFinancialAccountUuid"
                classNamePrefix="react-select"
                isClearable
                placeholder="(Use default operating cash)"
                options={this.financialAccountOptions}
                value={this.selectedFinancialAccountOption}
                onChange={(option) => this.onChange({ target: { name: "financialAccountUuid", value: option?.value || null } })}
              />
              <Form.Label>PAID FROM</Form.Label>
            </div>
          </Col>

          <Col xs="12">
            <div className="mb-3 top-label">
              <Form.Control
                as="textarea"
                rows={3}
                name="memo"
                value={d.memo || ""}
                onChange={this.onChange}
              />
              <Form.Label>MEMO</Form.Label>
            </div>
          </Col>

        </Row>
      </Modal>
    );
  }
}

BusinessExpenseModal.propTypes = {
  show: PropTypes.bool,
  onCancel: PropTypes.func,
  onSaved: PropTypes.func,
  defaultExpenseAccountUuid: PropTypes.string,
  defaultFinancialAccountUuid: PropTypes.string,
  defaultAccountingAccountUuid: PropTypes.string,
  auth: PropTypes.object,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

export default connect(mapStateToProps, {
  loading,
  getAccounts,
  getFinancialAccounts,
  getSetting,
  renderReport,
  saveBusinessExpense,
  searchVendors,
  getNextCheckNumber,
  getCheckCollisions,
  markPrinted,
})(BusinessExpenseModal);
